import { recreateTransform } from "@technik-sde/prosemirror-recreate-transform"
import { Step, Transform } from "@tiptap/pm/transform"

import type { Options } from "@technik-sde/prosemirror-recreate-transform"
import type { Node } from "@tiptap/pm/model"

// Past this many LCS cells the changed middle is diffed as one region.
const MAX_LCS_CELLS = 2_000_000

const POSITION_KEYS = ["from", "to", "gapFrom", "gapTo", "pos"] as const

interface Region {
	fromStart: number
	fromEnd: number
	toStart: number
	toEnd: number
}

// recreateTransform took 38s on edits scattered through a 390-block doc, so
// match top-level blocks first and hand it only the changed runs.
export function recreateTransformBlockwise(
	fromDoc: Node,
	toDoc: Node,
	options: Options,
): Transform {
	try {
		const tr = diffChangedRegions(fromDoc, toDoc, options)
		if (tr.doc.eq(toDoc)) return tr
	} catch {
		// Fall through to the whole-doc diff.
	}
	return recreateTransform(fromDoc, toDoc, options)
}

function diffChangedRegions(
	fromDoc: Node,
	toDoc: Node,
	options: Options,
): Transform {
	const fromBlocks = childrenOf(fromDoc)
	const toBlocks = childrenOf(toDoc)
	const offsets = [0]
	for (const block of fromBlocks) {
		offsets.push(offsets[offsets.length - 1]! + block.nodeSize)
	}

	const tr = new Transform(fromDoc)
	const regions = changedRegions(fromBlocks, toBlocks)
	// Back to front, so each region's start position in fromDoc stays valid.
	for (let r = regions.length - 1; r >= 0; r--) {
		const { fromStart, fromEnd, toStart, toEnd } = regions[r]!
		const pos = offsets[fromStart]!
		const inserted = toBlocks.slice(toStart, toEnd)
		if (fromStart === fromEnd) {
			tr.insert(pos, inserted)
			continue
		}
		if (toStart === toEnd) {
			tr.delete(pos, offsets[fromEnd]!)
			continue
		}
		const sub = recreateTransform(
			fromDoc.type.create(fromDoc.attrs, fromBlocks.slice(fromStart, fromEnd)),
			toDoc.type.create(toDoc.attrs, inserted),
			options,
		)
		for (const step of sub.steps) {
			const shifted = Step.fromJSON(
				fromDoc.type.schema,
				shiftStepJson(step.toJSON() as Record<string, unknown>, pos),
			)
			const result = tr.maybeStep(shifted)
			if (result.failed) throw new Error(result.failed)
		}
	}
	return tr
}

function childrenOf(doc: Node): Node[] {
	const out: Node[] = []
	doc.forEach((child) => out.push(child))
	return out
}

function shiftStepJson(
	json: Record<string, unknown>,
	offset: number,
): Record<string, unknown> {
	const shifted = { ...json }
	for (const key of POSITION_KEYS) {
		const value = shifted[key]
		if (typeof value === "number") shifted[key] = value + offset
	}
	return shifted
}

function changedRegions(fromBlocks: Node[], toBlocks: Node[]): Region[] {
	const ids = new Map<string, number>()
	const keyOf = (node: Node) => {
		const key = JSON.stringify(node.toJSON())
		let id = ids.get(key)
		if (id === undefined) {
			id = ids.size
			ids.set(key, id)
		}
		return id
	}
	const a = fromBlocks.map(keyOf)
	const b = toBlocks.map(keyOf)

	let start = 0
	while (start < a.length && start < b.length && a[start] === b[start]) start++
	let endA = a.length
	let endB = b.length
	while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
		endA--
		endB--
	}
	if (start === endA && start === endB) return []

	const n = endA - start
	const m = endB - start
	if (n === 0 || m === 0 || n * m > MAX_LCS_CELLS) {
		return [{ fromStart: start, fromEnd: endA, toStart: start, toEnd: endB }]
	}

	// suffix[i][j] = LCS length of a[start+i..endA) and b[start+j..endB).
	const width = m + 1
	const suffix = new Uint32Array((n + 1) * width)
	for (let i = n - 1; i >= 0; i--) {
		for (let j = m - 1; j >= 0; j--) {
			suffix[i * width + j] =
				a[start + i] === b[start + j]
					? suffix[(i + 1) * width + j + 1]! + 1
					: Math.max(suffix[(i + 1) * width + j]!, suffix[i * width + j + 1]!)
		}
	}

	const regions: Region[] = []
	let open: Region | null = null
	let i = 0
	let j = 0
	while (i < n || j < m) {
		if (i < n && j < m && a[start + i] === b[start + j]) {
			if (open) regions.push(open)
			open = null
			i++
			j++
			continue
		}
		open ??= {
			fromStart: start + i,
			fromEnd: start + i,
			toStart: start + j,
			toEnd: start + j,
		}
		if (
			j < m &&
			(i >= n || suffix[i * width + j + 1]! >= suffix[(i + 1) * width + j]!)
		) {
			j++
			open.toEnd = start + j
		} else {
			i++
			open.fromEnd = start + i
		}
	}
	if (open) regions.push(open)
	return regions
}
